import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Layout.css';

interface LayoutProps {
  children: React.ReactNode;
  showHeader?: boolean;
  showFooter?: boolean;
  headerVariant?: 'transparent' | 'solid';
}

const Layout: React.FC<LayoutProps> = ({
  children,
  showHeader = true,
  showFooter = true,
  headerVariant = 'solid'
}) => {
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  // Check if current page is homepage
  const isHomePage = location.pathname === '/';

  return (
    <div className="layout-container">
      {showHeader && (
        <header className={`layout-header ${headerVariant === 'transparent' ? 'transparent' : 'solid'}`}>
          <div className="header-content">
            <div className="logo-section">
              <Link to="/" className="logo-link">
                <div className="logo-text">🍅</div>
                <h1 className="app-title">Tomast</h1>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <nav className="desktop-nav">
              {isAuthenticated && user ? (
                <div className="user-nav">
                  <Link to="/dashboard" className="nav-link dashboard-link">
                    Dashboard
                  </Link>
                  <div className="user-info">
                    <span className="user-name">
                      Welcome, {user?.first_name || user?.username}!
                    </span>
                    <button
                      onClick={handleLogout}
                      className="logout-button"
                    >
                      Logout
                    </button>
                  </div>
                </div>
              ) : (
                <div className="auth-nav">
                  <Link to="/login" className="nav-link login-link">
                    Login
                  </Link>
                  <Link to="/register" className="nav-link register-link">
                    Sign Up
                  </Link>
                </div>
              )}
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="mobile-menu-button"
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
            >
              <span className={`hamburger ${mobileMenuOpen ? 'open' : ''}`}>
                <span></span>
                <span></span>
                <span></span>
              </span>
            </button>
          </div>

          {/* Mobile Navigation */}
          <nav className={`mobile-nav ${mobileMenuOpen ? 'open' : ''}`}>
            {isAuthenticated && user ? (
              <div className="mobile-user-nav">
                <div className="mobile-user-info">
                  <span className="mobile-user-name">
                    Welcome, {user?.first_name || user?.username}!
                  </span>
                </div>
                <Link
                  to="/dashboard"
                  className="mobile-nav-link"
                  onClick={closeMobileMenu}
                >
                  Dashboard
                </Link>
                <button
                  onClick={() => {
                    handleLogout();
                    closeMobileMenu();
                  }}
                  className="mobile-logout-button"
                >
                  Logout
                </button>
              </div>
            ) : (
              <div className="mobile-auth-nav">
                <Link
                  to="/login"
                  className="mobile-nav-link"
                  onClick={closeMobileMenu}
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="mobile-nav-link"
                  onClick={closeMobileMenu}
                >
                  Sign Up
                </Link>
              </div>
            )}
          </nav>
        </header>
      )}

      <main className={`layout-main ${headerVariant === 'transparent' ? 'with-transparent-header' : ''}`}>
        {children}
      </main>

      {showFooter && (
        <footer className="layout-footer">
          <div className="footer-content">
            <div className="footer-logo">
              <div className="logo-text">🍅</div>
              <span className="footer-title">Tomast</span>
            </div>
            <p className="footer-text">
              © 2024 Tomast. Boost your productivity with the Pomodoro Technique.
            </p>
            <div className="footer-links">
              {isAuthenticated ? (
                <Link to="/dashboard" className="footer-link">
                  Dashboard
                </Link>
              ) : (
                <>
                  <Link to="/login" className="footer-link">
                    Login
                  </Link>
                  <Link to="/register" className="footer-link">
                    Sign Up
                  </Link>
                </>
              )}
            </div>
          </div>
        </footer>
      )}
    </div>
  );
};

export default Layout;